import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  CircularProgress
} from '@mui/material';
import { Event as EventIcon } from '@mui/icons-material';
import { useThemeSettings } from '../../../../contexts/ThemeContext';

/**
 * Dialog for rescheduling an intervention
 */
const RescheduleDialog = ({
  open,
  onClose,
  onSubmit,
  intervention,
  rescheduling = false
}) => {
  const { t } = useThemeSettings();

  // Form state
  const [date, setDate] = useState('');
  const [time, setTime] = useState('09:00');
  const [notes, setNotes] = useState('');

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      // Pre-fill with current intervention date if available
      if (intervention?.scheduled_for || intervention?.scheduledFor) {
        const scheduledDate = new Date(intervention.scheduled_for || intervention.scheduledFor);
        setDate(scheduledDate.toISOString().split('T')[0]);
        setTime(scheduledDate.toTimeString().slice(0, 5));
      } else {
        setDate('');
        setTime('09:00');
      }
      setNotes('');
    }
  }, [open, intervention]);

  const handleSubmit = () => {
    onSubmit({
      date,
      time,
      notes: notes.trim() || null
    });
  };

  const handleClose = () => {
    if (!rescheduling) {
      setDate('');
      setTime('09:00');
      setNotes('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {t ? t("Tickets.reschedule_intervention") : "Reschedule Intervention"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t ? t("Tickets.reschedule_calendar_note") : "Change the scheduled date and time for this intervention. Calendar items and notifications will be updated automatically."}
        </DialogContentText>

        <TextField
          label={t ? t("Tickets.new_intervention_date") : "New Intervention Date"}
          type="date"
          fullWidth
          required
          value={date}
          onChange={(e) => setDate(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
          InputLabelProps={{ shrink: true }}
          helperText={t ? t("Tickets.select_new_date_intervention") : "Select the new date for the intervention"}
        />

        <TextField
          label={t ? t("Tickets.new_intervention_time") : "New Intervention Time"}
          type="time"
          fullWidth
          value={time}
          onChange={(e) => setTime(e.target.value)}
          sx={{ mb: 2 }}
          InputLabelProps={{ shrink: true }}
          helperText={t ? t("Tickets.select_new_time_intervention") : "Select the new time for the intervention"}
        />

        <TextField
          label={t ? t("Tickets.reschedule_reason_optional") : "Reschedule Reason (Optional)"}
          multiline
          rows={3}
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t ? t("Tickets.reschedule_reason_placeholder") : "Provide a reason for rescheduling (will be included in the notification email)..."}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={rescheduling}>
          {t ? t("Common.cancel") : "Cancel"}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={rescheduling || !date}
          startIcon={rescheduling ? <CircularProgress size={20} /> : <EventIcon />}
        >
          {rescheduling
            ? (t ? t("Tickets.rescheduling") : "Rescheduling...")
            : (t ? t("Tickets.reschedule") : "Reschedule")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RescheduleDialog;
